'use client'

import { useEffect, useState } from 'react'
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

export interface IndexNavProps {
  /** 1-based current position */
  current: number
  total: number
  onPrevious: () => void
  onNext: () => void
  /** If provided, renders a ChevronFirst button */
  onFirst?: () => void
  /** If provided, renders a ChevronLast button */
  onLast?: () => void
  /** If provided, the counter becomes an editable number input that calls onChange with a 1-based index */
  onChange?: (index: number) => void
  /** Show spread notation (e.g. "3-4") for non-edge pages */
  spreadMode?: boolean
  className?: string
}

export function IndexNav({
  current,
  total,
  onPrevious,
  onNext,
  onFirst,
  onLast,
  onChange,
  spreadMode,
  className,
}: IndexNavProps) {
  const isEdgePage = current === 1 || (total % 2 === 0 && current >= total)
  const spreadLabel = spreadMode && !isEdgePage ? `${current - 1}-${current}` : String(current)

  const [inputValue, setInputValue] = useState(spreadLabel)

  useEffect(() => {
    setInputValue(spreadLabel)
  }, [spreadLabel])

  const commit = (value: string) => {
    if (!onChange) return
    const parsed = parseInt(value.split('-')[0] ?? value, 10)
    const clamped = isNaN(parsed) ? 1 : Math.min(Math.max(parsed, 1), total)
    // user types left page; optimistic shows the spread they'll land on
    setInputValue(spreadMode && clamped !== 1 && clamped < total ? `${clamped}-${clamped + 1}` : String(clamped))
    onChange(clamped)
  }

  const hasPrevious = current > 1
  const hasNext = current < total

  return (
    <div
      className={cn(
        'srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:rounded-full srd:bg-navy-100 srd:py-1 srd:px-4 srd:h-12',
        className,
      )}
    >
      {onFirst && (
        <Button
          variant='ghost'
          className='srd:size-10 srd:p-0 srd:text-neutral-white srd:hover:bg-navy-80 srd:rounded-full'
          onClick={onFirst}
          disabled={!hasPrevious}
          aria-label='Pertama'
        >
          <ChevronFirst className='srd:size-5' />
        </Button>
      )}

      <Button
        variant='ghost'
        className='srd:size-10 srd:p-0 srd:text-neutral-white srd:hover:bg-navy-80 srd:rounded-full'
        onClick={onPrevious}
        disabled={!hasPrevious}
        aria-label='Sebelumnya'
      >
        <ChevronLeft className='srd:size-5' />
      </Button>

      {onChange ? (
        <input
          type={spreadMode ? 'text' : 'number'}
          min={1}
          max={total}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(e.currentTarget.value)
          }}
          className={cn(
            typographyVariants({ variant: 'label-md' }),
            'srd:text-neutral-white srd:border srd:border-outline srd:h-10 srd:rounded-lg srd:text-center srd:bg-transparent srd:outline-none srd:[appearance:textfield] srd:[&::-webkit-outer-spin-button]:appearance-none srd:[&::-webkit-inner-spin-button]:appearance-none',
            spreadMode && !isEdgePage ? 'srd:w-14' : 'srd:w-10',
          )}
        />
      ) : (
        <span
          className={cn(
            typographyVariants({ variant: 'label-md' }),
            'srd:text-neutral-white srd:min-w-12 srd:text-center',
          )}
        >
          {spreadLabel} / {total}
        </span>
      )}

      <Button
        variant='ghost'
        className='srd:size-10 srd:p-0 srd:text-neutral-white srd:hover:bg-navy-80 srd:rounded-full'
        onClick={onNext}
        disabled={!hasNext}
        aria-label='Berikutnya'
      >
        <ChevronRight className='srd:size-5' />
      </Button>

      {onLast && (
        <Button
          variant='ghost'
          className='srd:size-10 srd:p-0 srd:text-neutral-white srd:hover:bg-navy-80 srd:rounded-full'
          onClick={onLast}
          disabled={!hasNext}
          aria-label='Terakhir'
        >
          <ChevronLast className='srd:size-5' />
        </Button>
      )}
    </div>
  )
}
